import { useCallback, useMemo } from 'react';
import { useLocation } from 'react-router';

import { getScopeApprovalStatus } from '@kaiizen/kai-pro-api';
import { usePropertyProject, useScanEditingContextSlice } from '@kaiizen/kai-pro-api-react-state';

import { ANALYTICS_EVENTS, trackEvent } from '../analytics';
import { parseScanNavigationFromPathname, useNavigateToScan } from './kaiNavigation';

export const useScopeApprovalAction = () => {
  const { scanRoot, scan, scanMutators, baselineScanRoot, scanNotesByScanPartId } = useScanEditingContextSlice([
    'scanRoot',
    'scan',
    'scanMutators',
    'baselineScanRoot',
    'scanNotesByScanPartId',
  ]);
  const scanId = scan?.id;

  const scopeApprovalStatus = useMemo(
    () => (scanRoot && scanId ? getScopeApprovalStatus(scanRoot, scanId, scanNotesByScanPartId, baselineScanRoot) : undefined),
    [scanRoot, scanId, scanNotesByScanPartId, baselineScanRoot],
  );

  const navigateToScan = useNavigateToScan();
  const { pathname } = useLocation();
  const { refetch: refetchPropertyProject, getCurrent: getCurrentPropertyProject } = usePropertyProject(scan?.propertyProjectId);

  const approveAndPublish = useCallback(async () => {
    if (!scan) return;
    await scanMutators.update(scan.id, { scanLifecycleStatus: 'approved' });
    trackEvent(ANALYTICS_EVENTS.scanApproved, { scanId: scan.id });
    if (!scan.propertyProjectId) return;
    await refetchPropertyProject();
    const project = await getCurrentPropertyProject();
    const newLatestScanId = project?.latestScanId ?? project?.latestScan?.id;
    if (newLatestScanId && newLatestScanId !== scan.id) {
      /* Carry the reader's current scope selection forward. Group and task ids
         are stable across the parent→child fork; dropping them here was what
         reset KaiNavigator's yellow selection after final approval. */
      const preservedNavigation = parseScanNavigationFromPathname(pathname);
      navigateToScan({ scanId: newLatestScanId, ...preservedNavigation, replace: true });
    }
  }, [scan, scanMutators, refetchPropertyProject, getCurrentPropertyProject, navigateToScan, pathname]);

  return { scopeApprovalStatus, approveAndPublish };
};
